import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Award, Plus, Save, ShieldCheck, ShieldQuestion, Trash2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth';
import { cn, getInitials } from '@/lib/utils';
import {
  SPECIALTIES,
  SENIORITY_LEVELS,
  SENIORITY_LABELS,
  SENIORITY_DESCRIPTIONS,
  LANGUAGES,
  LANGUAGE_LABELS,
  type Specialty,
  type Seniority,
  type Language,
} from '@parecer/shared';
import { SpecialtyPicker } from '@/components/SpecialtyPicker';

interface CertificationProfile {
  id: string;
  title: string;
  issuer: string;
  issuedYear?: number;
  fileUrl?: string;
  verified?: boolean;
  verifiedAt?: string;
}

interface LawyerProfile {
  userId: string;
  name: string;
  email: string;
  oab: string;
  oabState: string;
  oabVerified: boolean;
  bio?: string;
  linkedinUrl?: string;
  specialties?: Specialty[];
  tags?: string[];
  seniority?: Seniority;
  languages?: Language[];
  servesIndividuals?: boolean;
  servesCompanies?: boolean;
  availableHoursPerWeek?: number;
  certifications?: CertificationProfile[];
  averageRating?: number;
  totalReviews?: number;
  totalCompletedTasks?: number;
}

const currentYear = new Date().getFullYear();

const formSchema = z.object({
  name: z.string().min(2, 'Nome muito curto').max(120),
  bio: z
    .string()
    .min(200, 'Bio mínima de 200 caracteres — descreva sua trajetória e diferenciais')
    .max(2000),
  linkedinUrl: z.string().url('URL inválida').or(z.literal('')).optional(),
  specialties: z
    .array(z.enum(SPECIALTIES))
    .min(1, 'Escolha ao menos uma especialidade')
    .max(15, 'Máximo de 15 especialidades'),
  tags: z.array(z.string().min(2).max(40)).max(20).optional(),
  languages: z.array(z.enum(LANGUAGES)).optional(),
  servesIndividuals: z.boolean().optional(),
  servesCompanies: z.boolean().optional(),
  availableHoursPerWeek: z
    .union([z.number().int().min(0).max(80), z.nan(), z.undefined()])
    .optional(),
  seniority: z.enum(SENIORITY_LEVELS).optional(),
  certifications: z
    .array(
      z.object({
        id: z.string().min(1),
        title: z.string().min(3, 'Título muito curto').max(200),
        issuer: z.string().min(2, 'Instituição').max(200),
        issuedYear: z
          .union([
            z.number().int().min(1950, 'Ano inválido').max(currentYear + 1, 'Ano no futuro'),
            z.nan(),
            z.undefined(),
          ])
          .optional(),
        fileUrl: z.string().url('URL inválida').or(z.literal('')).optional(),
        verified: z.boolean().optional(),
        verifiedAt: z.string().optional(),
      }),
    )
    .max(20, 'Máximo 20 certificações'),
});
type FormValues = z.infer<typeof formSchema>;

export function LawyerPerfil() {
  const userId = useAuthStore((s) => s.user?.userId);
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['users', 'me', 'lawyer'],
    queryFn: () => api.get<LawyerProfile>('/users/me'),
    enabled: Boolean(userId),
  });

  const [selectedSpecs, setSelectedSpecs] = useState<Specialty[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      bio: '',
      linkedinUrl: '',
      specialties: [],
      certifications: [],
    },
  });

  const certsField = useFieldArray({ control: form.control, name: 'certifications' });

  // Hydrate form once profile loads.
  useEffect(() => {
    if (!profile) return;
    const specs = (profile.specialties ?? []) as Specialty[];
    const tags = profile.tags ?? [];
    setSelectedSpecs(specs);
    setSelectedTags(tags);
    form.reset({
      name: profile.name ?? '',
      bio: profile.bio ?? '',
      linkedinUrl: profile.linkedinUrl ?? '',
      specialties: specs,
      tags,
      ...(profile.seniority ? { seniority: profile.seniority } : {}),
      languages: profile.languages ?? ['pt'],
      ...(profile.servesIndividuals !== undefined
        ? { servesIndividuals: profile.servesIndividuals }
        : { servesIndividuals: true }),
      ...(profile.servesCompanies !== undefined
        ? { servesCompanies: profile.servesCompanies }
        : { servesCompanies: true }),
      ...(profile.availableHoursPerWeek !== undefined
        ? { availableHoursPerWeek: profile.availableHoursPerWeek }
        : {}),
      certifications: (profile.certifications ?? []).map((c) => ({
        id: c.id,
        title: c.title,
        issuer: c.issuer,
        issuedYear: c.issuedYear,
        fileUrl: c.fileUrl ?? '',
        verified: c.verified,
        verifiedAt: c.verifiedAt,
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.userId]);

  function setSpecs(next: Specialty[]): void {
    setSelectedSpecs(next);
    form.setValue('specialties', next, { shouldValidate: true, shouldDirty: true });
  }
  function setTags(next: string[]): void {
    setSelectedTags(next);
    form.setValue('tags', next as never, { shouldValidate: true, shouldDirty: true });
  }

  function addCertification(): void {
    certsField.append({
      id: `cert-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: '',
      issuer: '',
      issuedYear: undefined,
      fileUrl: '',
      verified: false,
    });
  }

  const saveMut = useMutation({
    mutationFn: async (values: FormValues) => {
      const linkedinUrl = values.linkedinUrl?.trim() || undefined;
      // Strip empty optional fields before sending: backend validates URL and
      // year strictly, so we don't want to send '' or NaN.
      const certifications = values.certifications.map((c) => ({
        id: c.id,
        title: c.title.trim(),
        issuer: c.issuer.trim(),
        ...(typeof c.issuedYear === 'number' && !isNaN(c.issuedYear) ? { issuedYear: c.issuedYear } : {}),
        ...(c.fileUrl?.trim() ? { fileUrl: c.fileUrl.trim() } : {}),
        verified: Boolean(c.verified),
      }));
      const tags = (values.tags ?? []).map((t) => t.trim()).filter(Boolean);
      const lawyerBody: Record<string, unknown> = {
        bio: values.bio,
        specialties: values.specialties,
        tags,
        certifications,
        servesIndividuals: Boolean(values.servesIndividuals),
        servesCompanies: Boolean(values.servesCompanies),
      };
      if (linkedinUrl) lawyerBody['linkedinUrl'] = linkedinUrl;
      if (values.seniority) lawyerBody['seniority'] = values.seniority;
      if (values.languages?.length) lawyerBody['languages'] = values.languages;
      if (
        typeof values.availableHoursPerWeek === 'number' &&
        !isNaN(values.availableHoursPerWeek)
      ) {
        lawyerBody['availableHoursPerWeek'] = values.availableHoursPerWeek;
      }
      await Promise.all([
        api.patch('/users/me', { name: values.name }),
        api.patch('/lawyers/me', lawyerBody),
      ]);
    },
    onSuccess: () => {
      toast({ variant: 'success', title: 'Perfil atualizado' });
      qc.invalidateQueries({ queryKey: ['users', 'me'] });
      qc.invalidateQueries({ queryKey: ['lawyer'] });
    },
    onError: (err) =>
      toast({
        variant: 'danger',
        title: 'Falha ao salvar',
        description: err instanceof Error ? err.message : 'Erro inesperado',
      }),
  });

  const bio = form.watch('bio');
  const bioLen = (bio ?? '').length;
  const seniority = form.watch('seniority');

  return (
    <AppShell role="lawyers">
      <div className="max-w-3xl">
        <h1 className="font-serif text-display-lg">Meu perfil</h1>
        <p className="text-muted-foreground mt-2">
          Informações que aparecem no seu card e na página pública do marketplace.
        </p>

        {isLoading && <p className="text-sm text-muted-foreground mt-8">Carregando…</p>}

        {!isLoading && profile && (
          <form onSubmit={form.handleSubmit((v) => saveMut.mutate(v))} className="mt-6 space-y-6">
            {/* Identity */}
            <Card>
              <CardHeader className="py-5">
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center font-serif text-xl flex-none">
                    {getInitials(profile.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg">{profile.name}</CardTitle>
                    <CardDescription className="mt-0.5">
                      OAB/{profile.oabState} {profile.oab} · {profile.email}
                    </CardDescription>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {profile.oabVerified ? (
                        <Badge variant="success" className="gap-1">
                          <ShieldCheck className="h-3 w-3" /> OAB verificada
                        </Badge>
                      ) : (
                        <Badge variant="warning" className="gap-1">
                          <ShieldQuestion className="h-3 w-3" /> OAB pendente
                        </Badge>
                      )}
                      {seniority && (
                        <Badge variant="accent">{SENIORITY_LABELS[seniority]}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Identity edits */}
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="name">Nome de exibição</Label>
                  <Input id="name" className="mt-1.5" {...form.register('name')} />
                  {form.formState.errors.name && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.name.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="linkedinUrl">LinkedIn (opcional)</Label>
                  <Input
                    id="linkedinUrl"
                    type="url"
                    placeholder="https://linkedin.com/in/seu-perfil"
                    className="mt-1.5"
                    {...form.register('linkedinUrl')}
                  />
                  {form.formState.errors.linkedinUrl && (
                    <p className="text-xs text-danger mt-1">{form.formState.errors.linkedinUrl.message}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Seniority */}
            <Card>
              <CardHeader className="py-5">
                <CardTitle className="text-base">Nível de senioridade</CardTitle>
                <CardDescription>
                  Indique o estágio da sua carreira. Aparece no card público como referência —
                  Pleno e acima podem cobrar mais por hora.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {SENIORITY_LEVELS.map((s) => {
                    const active = seniority === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => form.setValue('seniority', s, { shouldDirty: true })}
                        className={cn(
                          'text-left rounded-md border p-3 transition-colors',
                          active
                            ? 'border-accent bg-accent/10'
                            : 'border-border hover:border-accent/40',
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{SENIORITY_LABELS[s]}</span>
                          {active && <Badge variant="accent" className="text-[10px]">Selecionado</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{SENIORITY_DESCRIPTIONS[s]}</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Bio */}
            <Card>
              <CardHeader className="py-5">
                <CardTitle className="text-base">Bio profissional</CardTitle>
                <CardDescription>
                  Trajetória, áreas, escritórios anteriores, formação, casos relevantes (sem PII).
                  Texto é a primeira coisa que clientes leem antes de te contratar — capriche.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  id="bio"
                  rows={8}
                  maxLength={2000}
                  className="flex w-full rounded-md border border-input bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Ex: Advogada com 12 anos de experiência em contratos comerciais e M&A. Sócia do escritório XYZ, com passagem por Mattos Filho. Mestre em Direito Empresarial pela USP."
                  {...form.register('bio')}
                />
                <div className="mt-1 flex items-center justify-between text-[11px]">
                  <span
                    className={cn(
                      'text-muted-foreground',
                      bioLen > 0 && bioLen < 200 && 'text-warning',
                      bioLen >= 200 && bioLen <= 2000 && 'text-success',
                    )}
                  >
                    {bioLen} / 2000 caracteres (mínimo 200)
                  </span>
                  {form.formState.errors.bio && (
                    <span className="text-danger">{form.formState.errors.bio.message}</span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Atendimento */}
            <Card>
              <CardHeader className="py-5">
                <CardTitle className="text-base">Atendimento</CardTitle>
                <CardDescription>
                  Idiomas, perfil de cliente (PF/PJ) e capacidade. Cliente filtra por isso no
                  marketplace.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Idiomas que você atende</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {LANGUAGES.map((lang) => {
                      const selected = (form.watch('languages') ?? []).includes(lang);
                      return (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => {
                            const cur = form.getValues('languages') ?? [];
                            const next = cur.includes(lang)
                              ? cur.filter((l) => l !== lang)
                              : [...cur, lang];
                            form.setValue('languages', next, { shouldDirty: true });
                          }}
                          className={cn(
                            'inline-flex items-center rounded-full border px-3 py-1.5 text-xs transition-colors',
                            selected
                              ? 'border-accent bg-accent/15 text-foreground font-medium'
                              : 'border-border text-muted-foreground hover:border-accent/40',
                          )}
                        >
                          {LANGUAGE_LABELS[lang]}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="flex items-start gap-3 rounded-md border border-border/60 p-3 cursor-pointer hover:border-accent/40 transition-colors">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-accent"
                      checked={Boolean(form.watch('servesIndividuals'))}
                      onChange={(e) =>
                        form.setValue('servesIndividuals', e.target.checked, {
                          shouldDirty: true,
                        })
                      }
                    />
                    <span className="text-sm">
                      <strong>Pessoa Física (PF)</strong>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        Atende clientes pessoa física.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 rounded-md border border-border/60 p-3 cursor-pointer hover:border-accent/40 transition-colors">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-accent"
                      checked={Boolean(form.watch('servesCompanies'))}
                      onChange={(e) =>
                        form.setValue('servesCompanies', e.target.checked, {
                          shouldDirty: true,
                        })
                      }
                    />
                    <span className="text-sm">
                      <strong>Pessoa Jurídica (PJ)</strong>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        Atende empresas — startups, SMBs, sociedades.
                      </span>
                    </span>
                  </label>
                </div>
                <div>
                  <Label htmlFor="availableHoursPerWeek">Disponibilidade semanal (horas)</Label>
                  <Input
                    id="availableHoursPerWeek"
                    type="number"
                    min={0}
                    max={80}
                    placeholder="20"
                    className="mt-1.5 max-w-[200px]"
                    {...form.register('availableHoursPerWeek', {
                      setValueAs: (v) => (v === '' || v == null ? undefined : Number(v)),
                    })}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Quantas horas você consegue dedicar à plataforma por semana. Aparece como
                    referência no card e influencia matching da IA.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Specialties + tags */}
            <Card>
              <CardHeader className="py-5">
                <CardTitle className="text-base">Especialidades e nichos</CardTitle>
                <CardDescription>
                  Escolha até 15 áreas de atuação (organizadas em 15 grandes áreas).
                  Tags livres no rodapé pra nichos hiper-específicos.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SpecialtyPicker
                  selected={selectedSpecs}
                  onChange={setSpecs}
                  tags={selectedTags}
                  onTagsChange={setTags}
                />
                {form.formState.errors.specialties && (
                  <p className="text-xs text-danger mt-2">{form.formState.errors.specialties.message}</p>
                )}
              </CardContent>
            </Card>

            {/* Certifications */}
            <Card>
              <CardHeader className="py-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Award className="h-4 w-4 text-accent" />
                      Certificações e especializações
                    </CardTitle>
                    <CardDescription>
                      LL.M., MBA, especializações OAB, certificações ESG, etc. Cada item passa por
                      verificação do admin antes de ganhar o selo "verificada" no perfil público —
                      você pode anexar um link público pro diploma/certificado pra acelerar a análise.
                    </CardDescription>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addCertification} className="gap-1.5">
                    <Plus className="h-4 w-4" /> Adicionar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {certsField.fields.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Nenhuma certificação cadastrada ainda. Comprovações elevam sua credibilidade no
                    marketplace e justificam preço-prêmio em áreas especializadas.
                  </p>
                )}
                {certsField.fields.map((field, idx) => {
                  const verified = form.watch(`certifications.${idx}.verified`);
                  return (
                    <div
                      key={field.id}
                      className="rounded-md border border-border/60 p-4 space-y-3 bg-muted/10"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {verified ? (
                            <Badge variant="success" className="gap-1 text-[10px]">
                              <ShieldCheck className="h-3 w-3" /> Verificada
                            </Badge>
                          ) : (
                            <Badge variant="warning" className="gap-1 text-[10px]">
                              <ShieldQuestion className="h-3 w-3" /> Aguardando verificação
                            </Badge>
                          )}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => certsField.remove(idx)}
                          className="text-danger hover:text-danger gap-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Remover
                        </Button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2">
                          <Label>Título</Label>
                          <Input
                            placeholder="Ex: LL.M. em Direito Empresarial"
                            className="mt-1"
                            {...form.register(`certifications.${idx}.title`)}
                          />
                          {form.formState.errors.certifications?.[idx]?.title && (
                            <p className="text-xs text-danger mt-1">
                              {form.formState.errors.certifications[idx]?.title?.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label>Instituição emissora</Label>
                          <Input
                            placeholder="Ex: FGV, USP, IBMEC"
                            className="mt-1"
                            {...form.register(`certifications.${idx}.issuer`)}
                          />
                          {form.formState.errors.certifications?.[idx]?.issuer && (
                            <p className="text-xs text-danger mt-1">
                              {form.formState.errors.certifications[idx]?.issuer?.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label>Ano de conclusão (opcional)</Label>
                          <Input
                            type="number"
                            min={1950}
                            max={currentYear + 1}
                            placeholder={String(currentYear)}
                            className="mt-1"
                            {...form.register(`certifications.${idx}.issuedYear`, {
                              setValueAs: (v) => (v === '' || v == null ? undefined : Number(v)),
                            })}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label>Link pro comprovante (opcional)</Label>
                          <Input
                            type="url"
                            placeholder="https://drive.google.com/... ou link público do diploma"
                            className="mt-1"
                            {...form.register(`certifications.${idx}.fileUrl`)}
                          />
                          {form.formState.errors.certifications?.[idx]?.fileUrl && (
                            <p className="text-xs text-danger mt-1">
                              {form.formState.errors.certifications[idx]?.fileUrl?.message}
                            </p>
                          )}
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Upload direto de arquivo virá em breve. Por enquanto, use um link público
                            (Drive, OneDrive, site da instituição). O admin valida pelo link antes de marcar como verificada.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Save bar */}
            <div className="sticky bottom-4 flex items-center justify-end gap-3 bg-background/80 backdrop-blur border border-border/60 rounded-lg p-3 shadow-sm">
              <p className="text-xs text-muted-foreground flex-1">
                Alterações vão pro perfil público em <span className="font-mono">/advogados/{profile.userId.slice(0, 8)}…</span>{' '}
                assim que você salvar.
              </p>
              <Button type="submit" variant="accent" loading={saveMut.isPending} className="gap-2">
                <Save className="h-4 w-4" />
                Salvar perfil
              </Button>
            </div>
          </form>
        )}
      </div>
    </AppShell>
  );
}
